import React from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import Collapse from "@material-ui/core/Collapse";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import AddIcon from "@material-ui/icons/Add";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { getTokens, brandAlpha } from "../../styles/tokens";

/* Árvore de opções do chatbot (QueueModal → nível 1; ChatBots/options → níveis seguintes).
 * Só apresentação: número da opção, título, ações em ícone e os filhos recuados com linha guia. */

export const useBotTreeStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  const danger = t.mode === "dark" ? t.status.down.fg : t.semantic.error;
  return {
    tree: {
      display: "flex",
      flexDirection: "column",
      gap: 2,
      minWidth: 0,
      fontFamily: t.font,
    },
    node: { minWidth: 0 },
    head: { display: "flex", alignItems: "flex-start", gap: 10, minWidth: 0, padding: "3px 0" },
    badge: {
      flex: "none",
      display: "grid",
      placeItems: "center",
      width: 24,
      height: 24,
      marginTop: 5,
      borderRadius: "50%",
      fontSize: 12,
      fontWeight: 600,
      fontVariantNumeric: "tabular-nums",
      backgroundColor: t.status.neutral.bg,
      color: t.status.neutral.fg,
    },
    badgeActive: { backgroundColor: t.brand, color: t.brandOn },
    badgeAdd: {
      backgroundColor: "transparent",
      border: `1px dashed ${t.lineStrong}`,
      color: t.textSecondary,
      "& .MuiSvgIcon-root": { fontSize: 15 },
    },
    headMain: { flex: 1, minWidth: 0 },
    titleRow: { display: "flex", alignItems: "center", gap: 2, minHeight: 34, minWidth: 0 },
    // botão de verdade (teclado), sem aparência de botão; o clique sobe para o nó, que abre a opção
    title: {
      display: "inline-flex",
      alignItems: "center",
      gap: 2,
      minWidth: 0,
      margin: "0 4px 0 -6px",
      padding: "5px 4px 5px 6px",
      border: 0,
      borderRadius: t.radius.badge,
      background: "none",
      font: "inherit",
      fontSize: 13.5,
      fontWeight: 600,
      lineHeight: 1.4,
      textAlign: "left",
      color: t.text,
      cursor: "pointer",
      overflowWrap: "anywhere",
      "&:hover": { backgroundColor: t.rowHover },
      "&:focus-visible": { outline: `2px solid ${brandAlpha(t.brand, 0.45)}`, outlineOffset: 1 },
    },
    chevron: { flex: "none", fontSize: 18, color: t.textMuted, transition: "transform 150ms ease" },
    chevronOpen: { transform: "rotate(180deg)" },
    // filhos recuados; a linha guia desce do centro do número
    children: {
      display: "flex",
      flexDirection: "column",
      gap: 8,
      minWidth: 0,
      marginLeft: 11.5,
      padding: "2px 0 10px 21px",
      borderLeft: `1px solid ${t.line}`,
      [theme.breakpoints.down("xs")]: { paddingLeft: 12 },
    },
    panel: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr)",
      gap: 12,
      minWidth: 0,
      padding: 12,
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.md,
      backgroundColor: t.muted,
      "& .MuiFormControl-root": { margin: 0 },
      "& .MuiOutlinedInput-root": { backgroundColor: t.paper },
    },
    panelRow: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: "4px 12px", minWidth: 0 },
    panelGrow: { flex: "1 1 auto" },
    check: {
      margin: 0,
      "& .MuiFormControlLabel-label": { fontSize: 13, color: t.text },
      "& .MuiCheckbox-root": { padding: 6, marginLeft: -6 },
    },
    actions: { display: "flex", alignItems: "center", gap: 2, flex: "none" },
    iconBtn: {
      width: 32,
      height: 32,
      padding: 0,
      color: t.textSecondary,
      "&:hover": { color: t.text },
      "& .MuiSvgIcon-root": { fontSize: 18 },
    },
    iconBtnDanger: { "&:hover": { color: danger, backgroundColor: brandAlpha(t.semantic.error, 0.08) } },
    message: { display: "flex", alignItems: "flex-start", gap: 8, minWidth: 0 },
    messageBody: { flex: "1 1 auto", minWidth: 0 },
    messageLabel: {
      display: "flex",
      alignItems: "center",
      gap: 4,
      fontSize: 12,
      fontWeight: 600,
      lineHeight: 1.4,
      color: t.textSecondary,
    },
    messageText: {
      marginTop: 2,
      fontSize: 13.5,
      lineHeight: 1.5,
      color: t.text,
      whiteSpace: "pre-wrap",
      overflowWrap: "anywhere",
    },
    messageEmpty: { color: t.textMuted },
    helpIcon: { fontSize: 16, color: t.textMuted, cursor: "help", verticalAlign: "middle" },
    add: {
      display: "inline-flex",
      alignItems: "center",
      alignSelf: "flex-start",
      gap: 10,
      padding: "3px 8px 3px 0",
      border: 0,
      borderRadius: t.radius.badge,
      background: "none",
      font: "inherit",
      fontSize: 13,
      fontWeight: 600,
      color: t.textSecondary,
      cursor: "pointer",
      "&:hover": { color: t.text },
      "&:hover $badgeAdd": { borderColor: t.textSecondary, color: t.text },
      "&:focus-visible": { outline: `2px solid ${brandAlpha(t.brand, 0.45)}`, outlineOffset: 1 },
    },
    loading: { padding: "4px 0", fontSize: 12.5, color: t.textMuted },
  };
});

export function BotTree({ className, children }) {
  const c = useBotTreeStyles();
  return <div className={clsx(c.tree, className)}>{children}</div>;
}

/**
 * Uma opção da árvore. `onActivate` roda em qualquer clique dentro do nó (como o `Step` que havia aqui)
 * ou, com `headOnly`, só no cabeçalho (como um `StepLabel`).
 * `children` (mensagem + subopções) só fica montado enquanto a opção está aberta.
 */
export function BotNode({ number, active, onActivate, headOnly = false, header, children }) {
  const c = useBotTreeStyles();
  return (
    <div className={c.node} onClick={headOnly ? undefined : onActivate} data-bot-option={number}>
      <div className={c.head} onClick={headOnly ? onActivate : undefined}>
        <span className={clsx(c.badge, active && c.badgeActive)} aria-hidden="true">
          {number}
        </span>
        <div className={c.headMain}>{header}</div>
      </div>
      {children ? (
        <Collapse in={Boolean(active)} timeout="auto" unmountOnExit>
          <div className={c.children}>{children}</div>
        </Collapse>
      ) : null}
    </div>
  );
}

/** Título clicável da opção (abre/fecha pelo clique que sobe para o nó) + ações à direita do nome. */
export function BotTitle({ active, expandable = true, actions, children }) {
  const c = useBotTreeStyles();
  return (
    <div className={c.titleRow}>
      <button type="button" className={c.title} aria-expanded={expandable ? Boolean(active) : undefined}>
        {children}
        {expandable && <ExpandMoreIcon className={clsx(c.chevron, active && c.chevronOpen)} />}
      </button>
      {actions && <div className={c.actions}>{actions}</div>}
    </div>
  );
}

/** Ação em ícone com Tooltip e aria-label (o `span` mantém o Tooltip vivo com o botão desabilitado). */
export function BotIconButton({ title, danger = false, className, children, ...rest }) {
  const c = useBotTreeStyles();
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          aria-label={title}
          className={clsx(c.iconBtn, danger && c.iconBtnDanger, className)}
          {...rest}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

/** Última linha da árvore: adicionar opção neste nível. */
export function BotAdd({ label, onClick }) {
  const c = useBotTreeStyles();
  return (
    <button type="button" className={c.add} onClick={onClick}>
      <span className={clsx(c.badge, c.badgeAdd)} style={{ marginTop: 0 }} aria-hidden="true">
        <AddIcon />
      </span>
      {label}
    </button>
  );
}
